import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';
import './RespondDeletionRequest.css';

// Get base URL from api config
let API_BASE_URL;
if (typeof window !== 'undefined' && window.APP_CONFIG?.BACKEND_URL) {
  API_BASE_URL = window.APP_CONFIG.BACKEND_URL;
} else if (process.env.REACT_APP_ULSAAPP_BASE_URL) {
  API_BASE_URL = process.env.REACT_APP_ULSAAPP_BASE_URL;
} else if (window.location.protocol === 'https:') {
  API_BASE_URL = `${window.location.protocol}//${window.location.host}`;
} else {
  API_BASE_URL = 'http://localhost:5000';
}

const RespondDeletionRequest = () => {
  const { id: courseId } = useParams();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action'); // 'confirm' or 'update'
  const emailToken = searchParams.get('token'); // magic-link token (optional)
  const { token, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [actionValid, setActionValid] = useState(false);

  const goToMyCourses = useCallback(() => {
    if (isLoggedIn) {
      navigate('/my-courses');
    } else {
      navigate('/login?redirect=/my-courses');
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    // Validate action (we will NOT auto-open popup here; popup will open on user click)
    const ok = !!action && (action === 'confirm' || action === 'update');
    setActionValid(ok);
    setLoading(false);

    if (!ok) {
      try {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Action',
          text: 'Invalid response action. Please use the links from the email.',
        }).then(() => {
          navigate('/home');
        });
      } catch (e) {
        navigate('/home');
      }
    }
  }, [action, courseId, navigate]);

  const handleResponse = useCallback(async () => {
    // Two modes:
    // 1) Logged-in instructor: use protected endpoint
    // 2) Email magic-link: use public endpoint with emailToken
    if (!isLoggedIn && !emailToken) {
      // No auth and no token => cannot proceed
      try {
        await Swal.fire({
          icon: 'info',
          title: 'Login Required',
          text: 'Please login to submit your response, or use the latest email link.',
          confirmButtonText: 'Go to Login',
        });
      } catch (_e) {
        // ignore
      }
      navigate(`/login?redirect=/instructor/respond-deletion/${courseId}?action=${action}`);
      return;
    }

    setProcessing(true);
    try {
      let response;

      if (isLoggedIn && token) {
        const headers = {
          Authorization: `Bearer ${token}`,
        };
        response = await axios.post(
          `${API_BASE_URL}/api/courses/${courseId}/respond-deletion?action=${action}`,
          {},
          { headers }
        );
      } else {
        // Public no-login flow via email token
        response = await axios.post(
          `${API_BASE_URL}/api/courses/${courseId}/respond-deletion-public?action=${action}`,
          { token: emailToken }
        );
      }

      if (response.data.success) {
        if (action === 'confirm') {
          Swal.fire({
            icon: 'success',
            title: 'Deletion Confirmed',
            html: `
              <p>You have confirmed the deletion request.</p>
              <p style="margin-top: 10px; font-size: 14px; color: #666;">
                The admin will proceed with course deletion and enrolled students will be notified.
              </p>
            `,
            confirmButtonText: 'OK',
          }).then(() => {
            goToMyCourses();
          });
        } else if (action === 'update') {
          const deadline = response.data.updateDeadline
            ? new Date(response.data.updateDeadline).toLocaleDateString()
            : 'N/A';
          Swal.fire({
            icon: 'success',
            title: 'Update Request Received',
            html: `
              <p>You have informed the admin that you will update the course.</p>
              <p style="margin-top: 10px; font-size: 14px; color: #666;">
                <strong>Update deadline:</strong> ${deadline}
              </p>
              <p style="margin-top: 10px; font-size: 14px; color: #856404;">
                You have 1 week to update your course through the platform.
              </p>
            `,
            confirmButtonText: 'Go to My Courses',
          }).then(() => {
            goToMyCourses();
          });
        }
      }
    } catch (error) {
      console.error('Error responding to deletion request:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to process your response. Please try again.',
        confirmButtonText: 'OK',
      }).then(() => {
        goToMyCourses();
      });
    } finally {
      setProcessing(false);
    }
  }, [isLoggedIn, token, emailToken, action, courseId, navigate, goToMyCourses]);

  const showConfirmationPopup = useCallback(() => {
    let title = '';
    let html = '';

    if (action === 'confirm') {
      title = 'Confirm Course Deletion';
      html = `
        <p style="margin-bottom: 10px;">
          Are you sure you want to <strong>permit deletion</strong> of this course?
        </p>
        <p style="font-size: 14px; color: #666; margin: 5px 0;">
          By confirming, you are giving permission to the admin to delete this course from the platform.
        </p>
        <p style="font-size: 14px; color: #d33; margin-top: 10px; font-weight: bold;">
          This action cannot be undone.
        </p>
      `;
    } else if (action === 'update') {
      title = 'I Will Update the Course';
      html = `
        <p style="margin-bottom: 10px;">
          Do you confirm that you are <strong>ready to update</strong> this course to address the issues mentioned by the admin?
        </p>
        <p style="font-size: 14px; color: #666; margin: 5px 0;">
          After confirming, you will have <strong>1 week</strong> to update the course content through the platform.
        </p>
        <p style="font-size: 14px; color: #856404; margin-top: 10px;">
          The admin will see your response and wait for your updates.
        </p>
      `;
    } else {
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Invalid Action',
        text: 'Invalid response action. Please use the links from the email.',
      }).then(() => {
        navigate('/home');
      });
      return;
    }

    try {
      Swal.fire({
        icon: 'question',
        title,
        html,
        showCancelButton: true,
        confirmButtonText: action === 'confirm' ? 'Yes, confirm deletion' : 'Yes, I will update',
        cancelButtonText: 'Cancel',
        confirmButtonColor: action === 'confirm' ? '#d33' : '#3498db',
        cancelButtonColor: '#6c757d',
      }).then((result) => {
        if (result.isConfirmed) {
          handleResponse();
        }
      });
    } catch (e) {
      // If SweetAlert fails, fallback to direct action
      const ok = window.confirm(action === 'confirm'
        ? 'Confirm deletion permission to admin?'
        : 'Confirm you will update the course within 1 week?');
      if (ok) handleResponse();
    }
  }, [action, handleResponse, navigate]);

  if (loading || processing) {
    return (
      <div className="respond-deletion-container">
        <div className="respond-deletion-loading">
          {processing ? 'Processing your response...' : 'Loading...'}
        </div>
      </div>
    );
  }

  if (!actionValid) return null;

  const isUpdate = action === 'update';
  const canReply = isLoggedIn || !!emailToken;

  return (
    <div className="respond-deletion-container">
      <div className="respond-deletion-card">
        <h2 className="respond-deletion-title">
          {isUpdate ? 'Respond: I Will Update the Course' : 'Respond: Confirm Deletion'}
        </h2>
        <p className="respond-deletion-text">
          {isUpdate
            ? 'Click the button below to confirm that you are ready to update this course. The admin will be notified and you will have 1 week to update the course via the platform.'
            : 'Click the button below to confirm that you permit the admin to delete this course from the platform.'}
        </p>
        {!canReply && (
          <div className="respond-deletion-login-box">
            <p className="respond-deletion-text" style={{ marginBottom: 10 }}>
              Login is required to submit your response (or use the latest email link).
            </p>
            <button
              type="button"
              className="respond-deletion-btn respond-deletion-btn-update"
              onClick={() => navigate(`/login?redirect=/instructor/respond-deletion/${courseId}?action=${action}`)}
            >
              Go to Login
            </button>
          </div>
        )}
        <div className="respond-deletion-actions">
          <button
            type="button"
            className={`respond-deletion-btn ${isUpdate ? 'respond-deletion-btn-update' : 'respond-deletion-btn-delete'}`}
            onClick={showConfirmationPopup}
            disabled={!canReply}
          >
            {isUpdate ? 'Reply: I Will Update' : 'Reply: Confirm Deletion'}
          </button>
          <button
            type="button"
            className="respond-deletion-btn respond-deletion-btn-cancel"
            onClick={() => navigate('/my-courses')}
          >
            Cancel
          </button>
        </div>
        <p className="respond-deletion-small">
          Course ID: <span className="respond-deletion-mono">{courseId}</span>
        </p>
      </div>
    </div>
  );
};

export default RespondDeletionRequest;
